import { apiFetch } from '../client';
import type {
    LoginRequestLocal,
    LoginResponseLocal,
    ResolveTenantsRequestLocal,
    ResolveTenantsResponseLocal,
    ChallengeRequestLocal,
    ChallengeResponseLocal,
    RefreshResponseLocal,
    LogoutResponseLocal,
    ForgotPasswordRequestLocal,
    ForgotPasswordResponseLocal,
    ConfirmForgotPasswordRequestLocal,
    VerifyPasswordResetOtpRequestLocal,
    VerifyPasswordResetOtpResponseLocal,
    ResendConfirmationRequestLocal,
    ResendOtpRequestLocal,
    ResendOtpResponseLocal,
    ChangePasswordRequestLocal,
    OtpRequestRequestLocal,
    OtpVerifyRequestLocal,
} from '../types/AuthLocal';

export const authApi = {
    /**
     * Warm-up: despierta la lambda de auth (PublicApi) sin efectos.
     * Se dispara al montar el login para que el `login` real no caiga por cold-start.
     */
    healthcheck: () => apiFetch('/healthcheck', { method: 'GET', skipRefresh: true }),
    /**
     * Identifier-first paso 1: resuelve los tenants de un identificador (email o teléfono, sin
     * password). DEC-RBAC-080: el front manda `identifier`; el backend acepta `identifier ?? email`.
     * Backend siempre responde 200 (anti-enumeración); 0 tenants → lista vacía.
     */
    resolveTenants: (body: ResolveTenantsRequestLocal) =>
        apiFetch<ResolveTenantsResponseLocal>('/auth/resolve-tenants', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    login: (body: LoginRequestLocal) =>
        apiFetch<LoginResponseLocal>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
    challenge: (body: ChallengeRequestLocal) =>
        apiFetch<ChallengeResponseLocal>('/auth/challenge', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    refresh: () =>
        apiFetch<RefreshResponseLocal>('/auth/refresh', { method: 'POST', skipRefresh: true }),
    logout: (body?: { cognitoSub: string; tenantId: string }) =>
        apiFetch<LogoutResponseLocal>('/auth/logout', {
            method: 'POST',
            body: body ? JSON.stringify(body) : undefined,
        }),
    forgotPassword: (body: ForgotPasswordRequestLocal) =>
        apiFetch<ForgotPasswordResponseLocal>('/auth/request-password-reset', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    /**
     * Paso 2 del reset: valida el OTP del correo y devuelve un `resetToken`
     * de un solo uso. 401 si el código es inválido o expiró.
     */
    verifyPasswordResetOtp: (body: VerifyPasswordResetOtpRequestLocal) =>
        apiFetch<VerifyPasswordResetOtpResponseLocal>('/auth/verify-password-reset-otp', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    confirmForgotPassword: (body: ConfirmForgotPasswordRequestLocal) =>
        apiFetch<{ success: true }>('/auth/confirm-password-reset', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    /**
     * Reenvío de OTP del challenge de login (DEC-RBAC-054). Re-dispara CUSTOM_AUTH (EMAIL_OTP)
     * para `{ email, tenantId }` SIN password y devuelve `{ session?, mfaMethod? }` frescos.
     * Respuesta anti-enumeración: 200 genérico siempre (un email/tenant inválido recibe {}).
     *
     * TODO: reemplazar por tipo generado tras regen openapi — el backend de /auth/resend-otp aún
     * NO está desplegado; tipado contra el contrato del spec §4 (ResendOtp*Local).
     */
    resendOtp: (body: ResendOtpRequestLocal) =>
        apiFetch<ResendOtpResponseLocal>('/auth/resend-otp', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    resendConfirmation: (body: ResendConfirmationRequestLocal) =>
        apiFetch<{ success: true }>('/auth/resend-confirmation', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    changePassword: (body: ChangePasswordRequestLocal) =>
        apiFetch<{ success: true }>('/auth/change-password', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    otpRequest: (body: OtpRequestRequestLocal) =>
        apiFetch<{ delivery: 'EMAIL' | 'SMS' }>('/auth/otp/request', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    otpVerify: (body: OtpVerifyRequestLocal) =>
        apiFetch<{ status: 'SUCCESS' }>('/auth/otp/verify', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
};
